import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import {
  text,
  surface,
  border,
  brand,
  interactive,
  status,
  fontSize,
  controlFontSize,
  fontWeight,
  fontFamily,
  spacing,
  borderRadius,
  duration,
  opacity,
  lineHeight,
  focusRing,
  layout,
  letterSpacing,
  dataViz,
} from "@atlas-int/tokens";

// ─── Types ────────────────────────────────────────────────────────────────────

type ButtonVariant = "default" | "primary" | "ghost" | "selector" | "icon" | "outline" | "gold" | "yAxis";
type ButtonSize = "xs" | "sm" | "md" | "lg";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Whether the button is in a selected/active state */
  selected?: boolean;
}

// ─── Size styles ──────────────────────────────────────────────────────────────

const sizeStyles = {
  xs: css`
    font-size: ${fontSize.xs};
    padding: ${spacing[1]} ${spacing[2]};
  `,
  sm: css`
    font-size: ${controlFontSize.sm};
    padding: ${spacing[1]} ${spacing[4]};
  `,
  md: css`
    font-size: ${controlFontSize.default};
    padding: ${spacing[2]} ${spacing[6]};
  `,
  lg: css`
    font-size: ${controlFontSize.default};
    padding: ${spacing[4]} ${spacing[12]};
  `,
};

// ─── Variant styles ───────────────────────────────────────────────────────────

const variantStyles = {
  default: css`
    background-color: ${surface.raised};
    color: ${text.primary};
    border: 1px solid ${border.strong};

    &:hover:not(:disabled) {
      background-color: ${surface.muted};
    }
  `,
  primary: css`
    background-color: ${interactive.default};
    color: ${text.onDark};
    border: none;

    &:hover:not(:disabled) {
      background-color: ${interactive.hover};
    }
  `,
  ghost: css`
    background: none;
    color: ${text.primary};
    border: none;

    &:hover:not(:disabled) {
      background-color: ${surface.hover};
    }
  `,
  selector: css`
    background: none;
    color: ${text.primary};
    border: none;

    &:hover:not(:disabled) {
      background-color: ${surface.hover};
    }
  `,
  outline: css`
    background: none;
    color: ${interactive.default};
    border: 1px solid ${interactive.default};

    &:hover:not(:disabled) {
      background-color: ${surface.hover};
    }
  `,
  icon: css`
    background: none;
    color: ${text.primary};
    border: none;
    padding: ${spacing[2]};

    &:hover:not(:disabled) {
      background-color: ${interactive.muted};
    }
  `,
  gold: css`
    background-color: ${status.gold};
    color: ${text.onDark};
    border: none;

    &:hover:not(:disabled) {
      background-color: ${status.goldHover};
    }
  `,
  yAxis: css`
    background-color: ${surface.input};
    color: ${text.primary};
    width: ${layout.controlsHeight};
    padding: 0 ${spacing[2]};
    border: 1px solid ${border.control};
    border-radius: ${borderRadius.base};
    letter-spacing: ${letterSpacing.tight};
    writing-mode: vertical-lr;
    transform: rotate(-180deg);
    text-transform: capitalize;
    font-size: ${controlFontSize.default};
    line-height: ${lineHeight.none};
    transition: border-color ${duration.fast};

    &:hover:not(:disabled) {
      border-color: ${text.primary};
      background-color: ${surface.input};
    }
  `,
};

const selectedStyle = css`
  background-color: ${brand.dark};
  color: ${text.onDark};

  &:hover:not(:disabled) {
    background-color: ${brand.dark};
  }
`;


// ─── Styled component ─────────────────────────────────────────────────────────

const StyledButton = styled.button<{
  $variant: ButtonVariant;
  $size: ButtonSize;
  $selected: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[2]};
  font-family: ${fontFamily.base};
  font-weight: ${fontWeight.normal};
  line-height: ${lineHeight.none};
  cursor: pointer;
  border-radius: ${borderRadius.base};
  transition: background-color ${duration.fast}, color ${duration.fast};
  box-sizing: border-box;
  white-space: nowrap;

  ${({ $variant }) => variantStyles[$variant]}
  ${({ $size }) => sizeStyles[$size]}
  ${({ $selected }) => ($selected ? selectedStyle : "")}

  &:focus-visible {
    outline: ${focusRing.width} ${focusRing.style} ${focusRing.color};
    outline-offset: ${focusRing.offset};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: ${opacity.muted};
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "default",
      size = "md",
      selected = false,
      children,
      ...rest
    },
    ref,
  ) => {
    return (
      <StyledButton
        ref={ref}
        $variant={variant}
        $size={size}
        $selected={selected}
        {...rest}
      >
        {children}
      </StyledButton>
    );
  },
);

Button.displayName = "Button";

export default Button;
export type { ButtonProps, ButtonVariant, ButtonSize };
